import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { TaxRateSelect } from '../components/TaxRateSelect.tsx';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, money, parseMoney, qty as fmtQty } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import type { Line, OrderView } from '../lib/types.ts';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { PayDialog } from './Pay.tsx';
import { downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * The hotel: the front desk (who arrives, who leaves, who is in), the reservations calendar,
 * the stays, housekeeping, the guests and their register, occupancy and revenue, and the
 * set-up of room types, rooms and season prices. A stay's room bill is paid on the till at
 * check-out, with the restaurant bills charged to the room on it.
 */

type StayState = 'booked' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show';
type Source = 'direct' | 'phone' | 'email' | 'walk_in' | 'website' | 'agency' | 'other';
type RoomStatus = 'clean' | 'dirty' | 'inspected';
type DocType = 'id_card' | 'passport' | 'other';
type Tab = 'desk' | 'calendar' | 'stays' | 'rooms' | 'guests' | 'reports' | 'setup';
type StayView = 'upcoming' | 'booked' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show' | 'all';

interface StayRow {
  id: string;
  number: number;
  state: StayState;
  arrival: string;
  departure: string;
  adults: number;
  children: number;
  source: Source;
  agency: string | null;
  reference: string | null;
  total: number;
  room: string | null;
  room_type: string;
  folio: string | null;
  note: string | null;
  guest: string;
  phone: string | null;
  type_name: string;
  room_number: string | null;
  bill: number;
  late?: boolean;
}
interface Desk {
  today: string;
  arrivals: StayRow[];
  departures: StayRow[];
  in_house: StayRow[];
  rooms: number;
  occupied: number;
  occupancy: number;
  dirty: number;
  out_of_order: number;
  arrivals_tomorrow: number;
  people: number;
  month: { rooms: number; extras: number };
}
interface RoomType {
  id: string;
  name: string;
  code: string;
  product: string;
  base_price: number;
  capacity: number;
  beds: string | null;
  description: string | null;
  sequence: number;
  active: boolean;
  vat?: number;
}
interface Room {
  id: string;
  number: string;
  type: string;
  floor: string | null;
  location: string | null;
  status: RoomStatus;
  cleaned_at: string | null;
  out_of_order: boolean;
  ooo_reason: string | null;
  note: string | null;
  active: boolean;
  stays?: number;
}
interface Rate {
  id: string;
  room_type: string;
  name: string;
  starts: string;
  ends: string;
  price: number;
  min_nights: number;
  active: boolean;
}
interface Setup {
  types: RoomType[];
  rooms: Room[];
  rates: Rate[];
  locations: { id: string; name: string }[];
}
interface Guest {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  nationality: string | null;
  document_type: DocType | null;
  document_no: string | null;
  birth_date: string | null;
  address: string | null;
  note: string | null;
}
interface Night {
  date: string;
  price: number;
  rate: string | null;
}
interface Stay {
  id: string;
  number: number;
  guest: string;
  room_type: string;
  room: string | null;
  arrival: string;
  departure: string;
  adults: number;
  children: number;
  state: StayState;
  source: Source;
  agency: string | null;
  reference: string | null;
  agreed_price: number | null;
  nights: Night[];
  total: number;
  note: string | null;
  folio: string | null;
  checked_in_at: string | null;
  checked_out_at: string | null;
  cancel_reason: string | null;
}
type FolioLine = Line & { hotel_night?: string | null; total: number; void_reason?: string | null };
interface StayDetail {
  stay: Stay;
  guest: Guest;
  guests: Guest[];
  type: RoomType;
  room: Room | null;
  folio: (OrderView & { lines: FolioLine[] }) | null;
  nights_due: Night[] | null;
  today: string;
  history: { at: string; kind: string; text: string | null; by_name: string | null }[];
}
interface Availability {
  nights: number;
  types: { type: RoomType; rooms: number; free: number; nights: Night[]; total: number; free_rooms: { id: string; number: string; floor: string | null; status: RoomStatus; out_of_order: boolean }[] }[];
}
interface Calendar {
  from: string;
  days: string[];
  today: string;
  types: RoomType[];
  rooms: Room[];
  stays: StayRow[];
  free: { type: string; days: number[] }[];
}
interface HkRoom extends Room {
  type_name: string;
  occupied: boolean;
  guest: string | null;
  people: number;
  leaving_today: boolean;
  arriving_today: boolean;
  task: 'service' | 'departure' | 'out_of_order' | null;
  cleaned_by_name: string | null;
}
interface Report {
  from: string;
  to: string;
  today: string;
  rooms: number;
  nights: number;
  sold: number;
  occupancy: number;
  revenue: number;
  adr: number;
  revpar: number;
  extras: number;
  on_books: number;
  revenue_on_books: number;
  average_stay: number;
  cancelled: number;
  no_shows: number;
  days: { date: string; rooms: number; sold: number; on_books: number; revenue: number }[];
  by_type: { type: string; name: string; rooms: number; sold: number; revenue: number; occupancy: number }[];
  by_source: { source: Source; stays: number; nights: number; revenue: number }[];
}
interface GuestRow extends Guest {
  stays: number;
  last_stay: string | null;
}
interface RegisterRow {
  number: number;
  arrival: string;
  departure: string;
  state: StayState;
  room: string | null;
  name: string;
  nationality: string | null;
  document_type: DocType | null;
  document_no: string | null;
  birth_date: string | null;
  address: string | null;
  main: boolean;
}

const SOURCES: Source[] = ['direct', 'phone', 'email', 'walk_in', 'website', 'agency', 'other'];
const DOCS: DocType[] = ['id_card', 'passport', 'other'];
const VIEWS: StayView[] = ['upcoming', 'booked', 'checked_in', 'checked_out', 'cancelled', 'no_show', 'all'];
const STATE: Record<StayState, { tone: Tone; icon: IconName }> = {
  booked: { tone: 'info', icon: 'calendar' },
  checked_in: { tone: 'ok', icon: 'bed' },
  checked_out: { tone: 'neutral', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
  no_show: { tone: 'warn', icon: 'alert' },
};
const ROOM: Record<RoomStatus, { tone: Tone; icon: IconName }> = {
  clean: { tone: 'ok', icon: 'sparkle' },
  dirty: { tone: 'warn', icon: 'alert' },
  inspected: { tone: 'info', icon: 'shieldCheck' },
};

const stayName = (n: number) => `R-${n}`;
const addDays = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
const between = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
const lek = (minor: number) => (minor ? String(minor / 100) : '');
const sourceLabel = (s: Source, agency?: string | null) => (s === 'agency' && agency ? agency : t(`htl.src_${s}` as MessageKey));
const docLabel = (d: DocType | null) => (d ? t(`htl.doc_${d}` as MessageKey) : '');
const shortDay = (d: string) => new Intl.DateTimeFormat(undefined, { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
const weekend = (d: string) => [0, 6].includes(new Date(`${d}T12:00:00Z`).getUTCDay());
const short = (d: string) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
const people = (s: { adults: number; children: number }) => [tn('htl.adults', s.adults), s.children ? tn('htl.children', s.children) : null].filter(Boolean).join(', ');

function StateBadge({ s, late }: { s: StayState; late?: boolean }) {
  if (late && s === 'booked') return <StatusBadge tone="warn" icon="clock" label={t('htl.st_late_arrival')} />;
  if (late && s === 'checked_in') return <StatusBadge tone="warn" icon="clock" label={t('htl.st_late_departure')} />;
  return <StatusBadge tone={STATE[s].tone} icon={STATE[s].icon} label={t(`htl.st_${s}` as MessageKey)} />;
}
function RoomBadge({ r }: { r: Pick<Room, 'status' | 'out_of_order'> }) {
  if (r.out_of_order) return <StatusBadge tone="danger" icon="alert" label={t('htl.rs_out_of_order')} />;
  return <StatusBadge tone={ROOM[r.status].tone} icon={ROOM[r.status].icon} label={t(`htl.rs_${r.status}` as MessageKey)} />;
}

export function Hotel() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const tabs: { key: Tab; icon: IconName; show: boolean }[] = [
    { key: 'desk', icon: 'key', show: can('hotel.view') && (can('hotel.book') || !can('hotel.housekeeping')) },
    { key: 'calendar', icon: 'calendar', show: can('hotel.view') && (can('hotel.book') || !can('hotel.housekeeping')) },
    { key: 'stays', icon: 'list', show: can('hotel.view') && (can('hotel.book') || !can('hotel.housekeeping')) },
    { key: 'rooms', icon: 'sparkle', show: can('hotel.view') },
    { key: 'guests', icon: 'users', show: can('hotel.guests') },
    { key: 'reports', icon: 'chart', show: can('hotel.reports') },
    { key: 'setup', icon: 'settings', show: can('hotel.configure') },
  ];
  const shown = tabs.filter((x) => x.show);
  const asked = params.get('tab') as Tab | null;
  const tab: Tab = shown.find((x) => x.key === asked)?.key ?? shown[0]?.key ?? 'rooms';
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };
  const [stamp, setStamp] = useState(0);
  const [form, setForm] = useState<null | { stay?: Stay; room?: string; arrival?: string }>(null);
  const changed = async () => setStamp((n) => n + 1);
  const openStay = params.get('stay');

  return (
    <Shell>
      <div className="page reports hotel">
        <div className="page-head">
          <div>
            <h1>{t('htl.title')}</h1>
            <p className="muted">{t('htl.subtitle')}</p>
          </div>
          {can('hotel.book') ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => setForm({})}>
                {t('htl.new_stay')}
              </Button>
            </div>
          ) : null}
        </div>

        <div className="segmented tabs htl-tabs" role="tablist" aria-label={t('htl.title')}>
          {shown.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === shown[0]!.key ? null : x.key, view: null })}>
              <Icon name={x.icon} size={16} />
              {t(`htl.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>

        {tab === 'desk' ? <FrontDesk key={stamp} onOpen={(id) => set({ stay: id })} /> : null}
        {tab === 'calendar' ? <CalendarView key={stamp} onOpen={(id) => set({ stay: id })} onBook={can('hotel.book') ? (room, arrival) => setForm({ room, arrival }) : undefined} /> : null}
        {tab === 'stays' ? <StayList key={stamp} view={(VIEWS.includes(params.get('view') as StayView) ? params.get('view') : 'upcoming') as StayView} onView={(v) => set({ view: v === 'upcoming' ? null : v })} onOpen={(id) => set({ stay: id })} /> : null}
        {tab === 'rooms' ? <Housekeeping key={stamp} /> : null}
        {tab === 'guests' ? <Guests key={stamp} onOpen={(id) => set({ stay: id })} /> : null}
        {tab === 'reports' ? <Reports /> : null}
        {tab === 'setup' ? <SetupView /> : null}
      </div>

      {openStay ? <StayDialog id={openStay} onClose={() => set({ stay: null })} onChange={changed} onEdit={(stay) => setForm({ stay })} /> : null}
      {form ? (
        <BookingForm
          stay={form.stay}
          room={form.room}
          arrival={form.arrival}
          onClose={() => setForm(null)}
          onDone={async (id) => {
            setForm(null);
            await changed();
            set({ stay: id });
          }}
        />
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Front desk */

function FrontDesk({ onOpen }: { onOpen: (id: string) => void }) {
  const { data: d } = useAction<Desk>('hotel.front_desk');
  if (!d) return <Skeleton h={320} r="var(--radius-lg)" />;
  const leaving = d.departures.length;
  const lateDep = d.departures.filter((s) => s.late).length;
  const lateArr = d.arrivals.filter((s) => s.late).length;
  return (
    <>
      <section className="rep-kpis" aria-label={t('htl.at_a_glance')}>
        <Kpi label={t('htl.k_occupancy')} value={`${d.occupancy}%`} hint={t('htl.k_occupancy_hint', { n: d.occupied, of: d.rooms - d.out_of_order })} />
        <Kpi label={t('htl.k_in_house')} value={String(d.occupied)} hint={tn('htl.k_people', d.people)} />
        <Kpi
          label={t('htl.k_arrivals')}
          value={String(d.arrivals.length)}
          hint={lateArr ? tn('htl.k_late_arrivals', lateArr) : tn('htl.k_tomorrow', d.arrivals_tomorrow)}
          warn={lateArr > 0}
        />
        <Kpi label={t('htl.k_departures')} value={String(leaving)} hint={lateDep ? tn('htl.k_late_departures', lateDep) : t('htl.k_departures_hint')} warn={lateDep > 0} />
        <Kpi
          label={t('htl.k_to_clean')}
          value={String(d.dirty)}
          hint={d.out_of_order ? tn('htl.k_out_of_order', d.out_of_order) : t('htl.k_to_clean_hint')}
          warn={d.dirty > 0}
        />
        <Kpi label={t('htl.k_month')} value={money(d.month.rooms)} hint={t('htl.k_month_hint', { amount: money(d.month.extras) })} />
      </section>
      <div className="htl-desk">
        <DeskList title={t('htl.arrivals')} icon="key" rows={d.arrivals} empty={t('htl.no_arrivals')} onOpen={onOpen} kind="arrival" />
        <DeskList title={t('htl.departures')} icon="logout" rows={d.departures} empty={t('htl.no_departures')} onOpen={onOpen} kind="departure" />
        <DeskList title={t('htl.in_house')} icon="bed" rows={d.in_house} empty={t('htl.nobody_in')} onOpen={onOpen} kind="in" />
      </div>
    </>
  );
}

function DeskList({ title, icon, rows, empty, onOpen, kind }: { title: string; icon: IconName; rows: StayRow[]; empty: string; onOpen: (id: string) => void; kind: 'arrival' | 'departure' | 'in' }) {
  return (
    <section className="panel htl-desk-list" aria-label={title}>
      <h2 className="htl-desk-title">
        <Icon name={icon} size={18} /> {title}
        <span className="count">{rows.length}</span>
      </h2>
      {rows.length ? (
        <div className="list-card">
          {rows.map((s) => (
            <button key={s.id} className="list-row" onClick={() => onOpen(s.id)}>
              <span className={`htl-room-chip${s.room_number ? '' : ' none'}`}>{s.room_number ?? '—'}</span>
              <span className="list-main">
                <span className="list-title">{s.guest}</span>
                <span className="list-sub">
                  {[
                    kind === 'arrival' ? t('htl.nights_until', { nights: tn('htl.nights', between(s.arrival, s.departure)), day: short(s.departure) }) : kind === 'departure' ? t('htl.since_until', { from: short(s.arrival), to: short(s.departure) }) : null,
                    s.type_name,
                    people(s),
                    s.source === 'agency' ? s.agency : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              {kind === 'arrival' ? (
                s.late ? <StateBadge s="booked" late /> : !s.room_number ? <span className="muted small">{t('htl.no_room_yet')}</span> : null
              ) : kind === 'departure' ? (
                <span className="tabular strong">{money(s.total + s.bill)}</span>
              ) : (
                <span className="muted small tabular">{t('htl.leaves', { day: short(s.departure) })}</span>
              )}
              <Icon name="chevronRight" size={18} />
            </button>
          ))}
        </div>
      ) : (
        <p className="muted htl-empty">{empty}</p>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- Calendar */

function CalendarView({ onOpen, onBook }: { onOpen: (id: string) => void; onBook?: (room: string, arrival: string) => void }) {
  const [from, setFrom] = useState<string | null>(null);
  const [days, setDays] = useState(14);
  const { data: first } = useAction<{ today: string }>('hotel.front_desk');
  const start = from ?? (first ? addDays(first.today, -1) : null);
  const [cal, setCal] = useState<Calendar | null>(null);
  const { fail } = useUi();
  useEffect(() => {
    if (!start) return;
    action<Calendar>('hotel.calendar', { from: start, days }).then(setCal, fail);
  }, [start, days, fail]);
  if (!cal || !start) return <Skeleton h={420} r="var(--radius-lg)" />;
  const n = cal.days.length;
  const col = (d: string) => Math.max(0, Math.min(n, between(cal.from, d)));
  const bar = (s: StayRow) => {
    const a = col(s.arrival);
    const b = col(s.departure);
    if (b <= a) return null;
    return (
      <button
        key={s.id}
        className={`htl-bar htl-bar-${s.state}${s.arrival < cal.from ? ' cut-start' : ''}${s.departure > addDays(cal.from, n) ? ' cut-end' : ''}`}
        style={{ gridColumn: `${a + 1} / ${b + 1}` }}
        onClick={() => onOpen(s.id)}
        title={`${stayName(s.number)} · ${s.guest} · ${day(s.arrival)} – ${day(s.departure)}`}
      >
        <span className="htl-bar-name">{s.guest}</span>
        <span className="htl-bar-sub">{people(s)}</span>
      </button>
    );
  };
  // Stays not given a room yet share lanes per type, side by side where they do not overlap.
  const lanes = (list: StayRow[]) => {
    const out: StayRow[][] = [];
    for (const s of [...list].sort((x, y) => x.arrival.localeCompare(y.arrival))) {
      const lane = out.find((l) => l.every((o) => o.departure <= s.arrival || o.arrival >= s.departure));
      if (lane) lane.push(s);
      else out.push([s]);
    }
    return out;
  };
  const grid = { gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` };
  return (
    <section className="panel htl-cal-panel">
      <div className="toolbar">
        <span className="row-actions">
          <Button size="sm" icon="back" onClick={() => setFrom(addDays(start, -7))} aria-label={t('htl.earlier')}>
            {t('htl.earlier')}
          </Button>
          <Button size="sm" onClick={() => setFrom(addDays(cal.today, -1))}>
            {t('htl.today')}
          </Button>
          <Button size="sm" onClick={() => setFrom(addDays(start, 7))} aria-label={t('htl.later')}>
            {t('htl.later')} <Icon name="chevronRight" size={14} />
          </Button>
          <input className="input htl-cal-date" type="date" value={start} onChange={(e) => e.target.value && setFrom(e.target.value)} aria-label={t('htl.from_day')} />
        </span>
        <div className="segmented" role="tablist" aria-label={t('htl.days_shown')}>
          {[14, 31].map((x) => (
            <button key={x} role="tab" aria-selected={days === x} className={days === x ? 'active' : ''} onClick={() => setDays(x)}>
              {tn('htl.n_days', x)}
            </button>
          ))}
        </div>
      </div>
      <div className="htl-cal-legend muted small">
        {(['booked', 'checked_in', 'checked_out'] as StayState[]).map((s) => (
          <span key={s}>
            <i className={`htl-dot htl-bar-${s}`} /> {t(`htl.st_${s}` as MessageKey)}
          </span>
        ))}
        {onBook ? <span>{t('htl.cal_click_hint')}</span> : null}
      </div>
      <div className="htl-cal" role="grid" aria-label={t('htl.tab_calendar')} style={{ '--cal-min': `${132 + n * (days > 14 ? 34 : 52)}px` } as CSSProperties}>
        <div className="htl-cal-row htl-cal-head" role="row">
          <span className="htl-cal-label" role="columnheader">
            {t('htl.room')}
          </span>
          <div className="htl-cal-cells" style={grid}>
            {cal.days.map((d) => (
              <span key={d} role="columnheader" className={`htl-cal-day${d === cal.today ? ' today' : ''}${weekend(d) ? ' weekend' : ''}`}>
                <small>{shortDay(d)}</small>
                <strong>{Number(d.slice(8))}</strong>
              </span>
            ))}
          </div>
        </div>
        {cal.types.map((ty) => {
          const rooms = cal.rooms.filter((r) => r.type === ty.id);
          const free = cal.free.find((f) => f.type === ty.id);
          const loose = cal.stays.filter((s) => s.room_type === ty.id && !s.room && s.state === 'booked' && s.departure > cal.from && s.arrival < addDays(cal.from, n));
          if (!rooms.length && !loose.length) return null;
          return (
            <div key={ty.id} className="htl-cal-group" role="rowgroup">
              <div className="htl-cal-row htl-cal-type" role="row">
                <span className="htl-cal-label">
                  <strong>{ty.name}</strong>
                  <span className="muted small">{t('htl.free_rooms')}</span>
                </span>
                <div className="htl-cal-cells" style={grid}>
                  {(free?.days ?? []).map((f, i) => (
                    <span key={i} className={`htl-cal-free${f <= 0 ? ' full' : ''}`}>
                      {f}
                    </span>
                  ))}
                </div>
              </div>
              {lanes(loose).map((lane, i) => (
                <div key={`l${i}`} className="htl-cal-row htl-cal-loose" role="row">
                  <span className="htl-cal-label muted small">{t('htl.no_room_yet')}</span>
                  <div className="htl-cal-cells" style={grid}>
                    {lane.map(bar)}
                  </div>
                </div>
              ))}
              {rooms.map((r) => (
                <div key={r.id} className="htl-cal-row" role="row">
                  <span className="htl-cal-label">
                    <strong>{r.number}</strong>
                    {r.out_of_order ? <Icon name="alert" size={14} className="text-danger" aria-label={t('htl.rs_out_of_order')} /> : r.status === 'dirty' ? <Icon name="alert" size={14} className="text-warn" aria-label={t('htl.rs_dirty')} /> : null}
                  </span>
                  <div className="htl-cal-cells" style={grid}>
                    {cal.days.map((d, i) =>
                      onBook && d >= cal.today ? (
                        <button key={d} className={`htl-cal-cell${weekend(d) ? ' weekend' : ''}`} style={{ gridColumn: `${i + 1}`, gridRow: 1 }} onClick={() => onBook(r.id, d)} aria-label={t('htl.book_room_on', { room: r.number, day: day(d) })} />
                      ) : (
                        <span key={d} className={`htl-cal-cell${weekend(d) ? ' weekend' : ''}`} style={{ gridColumn: `${i + 1}`, gridRow: 1 }} />
                      ),
                    )}
                    {cal.stays.filter((s) => s.room === r.id).map((s) => <span key={s.id} style={{ display: 'contents' }}>{bar(s)}</span>)}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Stays */

function StayList({ view, onView, onOpen }: { view: StayView; onView: (v: StayView) => void; onOpen: (id: string) => void }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  const { data } = useAction<{ today: string; stays: StayRow[] }>('hotel.stays', { state: query ? 'all' : view, q: query });
  const exportCsv = () =>
    data &&
    downloadCsv(
      'stays.csv',
      [t('htl.c_no'), t('htl.c_guest'), t('htl.c_room'), t('htl.arrival'), t('htl.departure'), t('htl.c_nights'), t('htl.c_people'), t('htl.source'), t('htl.reference'), t('htl.c_total'), t('htl.c_state')],
      data.stays.map((s) => [stayName(s.number), s.guest, s.room_number ?? s.type_name, s.arrival, s.departure, between(s.arrival, s.departure), s.adults + s.children, sourceLabel(s.source, s.agency), s.reference ?? '', lek(s.total) || '0', t(`htl.st_${s.state}` as MessageKey)]),
    );
  return (
    <>
      <div className="toolbar">
        <div className="segmented tabs" role="tablist" aria-label={t('htl.show')}>
          {VIEWS.map((v) => (
            <button key={v} role="tab" aria-selected={!query && view === v} className={!query && view === v ? 'active' : ''} onClick={() => (setQ(''), onView(v))}>
              {t(`htl.v_${v}` as MessageKey)}
            </button>
          ))}
        </div>
        <span className="row-actions">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('htl.search_stays')} aria-label={t('htl.search_stays')} />
          </label>
          <Button icon="download" onClick={exportCsv} disabled={!data?.stays.length}>
            {t('htl.export')}
          </Button>
        </span>
      </div>
      {!data ? (
        <Skeleton h={300} r="var(--radius-lg)" />
      ) : !data.stays.length ? (
        <EmptyState icon={query ? 'search' : 'calendar'} title={query ? t('htl.none_found') : t(`htl.empty_${view}` as MessageKey)} />
      ) : (
        <Table
          view="hotel.stays"
          head={[t('htl.c_no'), t('htl.c_guest'), t('htl.c_dates'), t('htl.c_room'), t('htl.c_total'), t('htl.c_state')]}
          phoneHide={[0, 3, 4]}
          left={[0, 1, 2]}
          rows={data.stays.map((s) => ({
            key: s.id,
            onClick: () => onOpen(s.id),
            cells: [
              <span className="tabular strong">{stayName(s.number)}</span>,
              <span className="vat-party">
                <span>{s.guest}</span>
                <span className="muted">{[people(s), sourceLabel(s.source, s.agency), s.reference].filter(Boolean).join(' · ')}</span>
              </span>,
              <span className="vat-party">
                <span className="tabular">
                  {short(s.arrival)} – {short(s.departure)}
                </span>
                <span className="muted">{tn('htl.nights', between(s.arrival, s.departure))}</span>
              </span>,
              <span>{s.room_number ? `${s.room_number} · ${s.type_name}` : s.type_name}</span>,
              <span className="tabular">{money(s.state === 'checked_in' ? s.total + s.bill : s.total)}</span>,
              <StateBadge s={s.state} late={(s.state === 'booked' && s.arrival < data.today) || (s.state === 'checked_in' && s.departure < data.today)} />,
            ],
          }))}
        />
      )}
    </>
  );
}

/* ---------------------------------------------------------------- One stay */

const H_TEXT = new Set(['cancelled', 'moved', 'charged', 'charge_removed', 'restaurant', 'out_of_order']);

function StayDialog({ id, onClose, onChange, onEdit }: { id: string; onClose: () => void; onChange: () => Promise<void>; onEdit: (s: Stay) => void }) {
  const { can } = useSession();
  const { toast } = useUi();
  const { data, reload } = useAction<StayDetail>('hotel.stay', { id });
  const { busy, run } = useSave();
  const [ask, setAsk] = useState<null | 'cancel' | 'no_show' | 'check_in' | 'move' | 'charge' | 'undo' | { remove: FolioLine } | { pay: OrderView }>(null);
  const done = async () => {
    setAsk(null);
    await Promise.all([reload(), onChange()]);
  };
  if (!data) {
    return (
      <Dialog title={t('htl.stay')} onClose={onClose} size="lg">
        <Skeleton h={300} />
      </Dialog>
    );
  }
  const s = data.stay;
  const book = can('hotel.book');
  const nights = between(s.arrival, s.departure);
  const lines = ((data.folio?.lines ?? []) as FolioLine[]).filter((l) => l.state === 'active' && !l.hotel_night);
  const extras = lines.reduce((sum, l) => sum + l.total, 0);
  const due = data.nights_due ?? [];
  const dueTotal = due.reduce((sum, n) => sum + n.price, 0);
  const late = (s.state === 'booked' && s.arrival < data.today) || (s.state === 'checked_in' && s.departure < data.today);
  const checkOut = () =>
    void run(async () => {
      const v = await action<OrderView>('hotel.prepare_check_out', { id: s.id });
      setAsk({ pay: v });
    });
  const nightLines = ((data.folio?.lines ?? []) as FolioLine[]).filter((l) => l.state === 'active' && l.hotel_night);
  return (
    <>
      <Dialog
        title={`${stayName(s.number)} · ${data.guest.name}`}
        onClose={onClose}
        size="lg"
        footer={
          book ? (
            <>
              <span className="foot-actions">
                {s.state === 'booked' ? (
                  <>
                    <Button variant="ghost" icon="x" onClick={() => setAsk('cancel')}>
                      {t('htl.cancel_stay')}
                    </Button>
                    {s.arrival <= data.today ? (
                      <Button variant="ghost" icon="alert" onClick={() => setAsk('no_show')}>
                        {t('htl.no_show')}
                      </Button>
                    ) : null}
                  </>
                ) : null}
                {s.state === 'checked_in' && !lines.length ? (
                  <Button variant="ghost" icon="undo" onClick={() => setAsk('undo')}>
                    {t('htl.undo_check_in')}
                  </Button>
                ) : null}
                {s.state === 'checked_in' ? (
                  <Button variant="ghost" icon="move" onClick={() => setAsk('move')}>
                    {t('htl.move')}
                  </Button>
                ) : null}
                {s.state === 'booked' || s.state === 'checked_in' ? (
                  <Button variant="ghost" icon="edit" onClick={() => onEdit(s)}>
                    {s.state === 'checked_in' ? t('htl.change_stay') : t('htl.edit')}
                  </Button>
                ) : null}
              </span>
              {s.state === 'booked' ? (
                <Button variant="primary" icon="key" onClick={() => setAsk('check_in')} disabled={s.arrival > data.today} title={s.arrival > data.today ? t('htl.not_due', { day: day(s.arrival) }) : undefined}>
                  {t('htl.check_in')}
                </Button>
              ) : null}
              {s.state === 'checked_in' ? (
                <>
                  <Button icon="plus" onClick={() => setAsk('charge')}>
                    {t('htl.add_extra')}
                  </Button>
                  {can('pos.pay') ? (
                    <Button variant="primary" icon="logout" onClick={checkOut} busy={busy}>
                      {t('htl.check_out')}
                    </Button>
                  ) : null}
                </>
              ) : null}
            </>
          ) : undefined
        }
      >
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('htl.room')}</span>
              <strong>{data.room ? `${data.room.number} · ${data.type.name}` : data.type.name}</strong>
            </div>
            <div>
              <span className="muted">{t('htl.c_dates')}</span>
              <strong className="tabular">
                {day(s.arrival)} – {day(s.departure)}
              </strong>
            </div>
            <div>
              <span className="muted">{s.state === 'checked_in' ? t('htl.to_pay_today') : t('htl.c_total')}</span>
              <strong className="tabular">{money(s.state === 'checked_in' ? dueTotal + (data.folio?.order.total ?? 0) - nightLines.reduce((x, l) => x + l.total, 0) : s.state === 'checked_out' ? (data.folio?.order.total ?? s.total) : s.total)}</strong>
            </div>
            <StateBadge s={s.state} late={late} />
          </div>

          {late && s.state === 'booked' ? (
            <p className="callout callout-warn">
              <Icon name="clock" size={16} />
              <span>{t('htl.late_arrival_note', { day: day(s.arrival) })}</span>
            </p>
          ) : null}
          {late && s.state === 'checked_in' ? (
            <p className="callout callout-warn">
              <Icon name="clock" size={16} />
              <span>{t('htl.late_departure_note', { day: day(s.departure) })}</span>
            </p>
          ) : null}
          {s.state === 'cancelled' ? (
            <p className="callout">
              <Icon name="x" size={16} />
              <span>{[t('htl.cancelled_note'), s.cancel_reason].filter(Boolean).join(' · ')}</span>
            </p>
          ) : null}
          {data.room?.out_of_order && s.state === 'booked' ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>{t('htl.room_ooo_note', { room: data.room.number, reason: data.room.ooo_reason ?? '' })}</span>
            </p>
          ) : null}

          <dl className="appr-facts">
            <dt>{t('htl.c_nights')}</dt>
            <dd>{tn('htl.nights', nights)}</dd>
            <dt>{t('htl.c_people')}</dt>
            <dd>{people(s)}</dd>
            <dt>{t('htl.source')}</dt>
            <dd>{[sourceLabel(s.source, s.agency), s.reference].filter(Boolean).join(' · ')}</dd>
            {s.agreed_price !== null ? (
              <>
                <dt>{t('htl.agreed_price')}</dt>
                <dd className="tabular">{t('htl.per_night', { amount: money(s.agreed_price) })}</dd>
              </>
            ) : null}
            {data.guest.phone || data.guest.email ? (
              <>
                <dt>{t('htl.contact')}</dt>
                <dd>{[data.guest.phone, data.guest.email].filter(Boolean).join(' · ')}</dd>
              </>
            ) : null}
            {s.checked_in_at ? (
              <>
                <dt>{t('htl.checked_in_at')}</dt>
                <dd>{dateTime(s.checked_in_at)}</dd>
              </>
            ) : null}
            {s.checked_out_at ? (
              <>
                <dt>{t('htl.checked_out_at')}</dt>
                <dd>{dateTime(s.checked_out_at)}</dd>
              </>
            ) : null}
            {s.note ? (
              <>
                <dt>{t('htl.note')}</dt>
                <dd>{s.note}</dd>
              </>
            ) : null}
          </dl>

          <section className="item-moves">
            <h3>{t('htl.guests_in_room')}</h3>
            <div className="list-card">
              {[data.guest, ...data.guests].map((g, i) => (
                <div key={g.id} className="list-row compact-move">
                  <span className="list-icon" aria-hidden="true">
                    <Icon name={i === 0 ? 'star' : 'users'} size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">
                      {g.name}
                      {i === 0 ? <span className="muted small"> · {t('htl.main_guest')}</span> : null}
                    </span>
                    <span className="list-sub">{[g.nationality, g.document_no ? `${docLabel(g.document_type)} ${g.document_no}` : can('hotel.guests') ? t('htl.no_document') : null, g.birth_date ? day(g.birth_date) : null].filter(Boolean).join(' · ')}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="item-moves">
            <h3>{s.state === 'checked_out' ? t('htl.bill_paid', { n: data.folio?.order.receipt_no ?? '' }) : t('htl.bill')}</h3>
            {s.state === 'booked' || s.state === 'cancelled' || s.state === 'no_show' ? (
              <NightsTable nights={s.nights} title={t('htl.price_per_night')} />
            ) : (
              <div className="htl-folio">
                {s.state === 'checked_in' ? (
                  <NightsTable nights={due} title={t('htl.nights_so_far')} />
                ) : (
                  nightLines.map((l) => <FolioRow key={l.id} line={l} />)
                )}
                {lines.length ? (
                  <>
                    <h4 className="htl-folio-sub">{t('htl.extras_and_restaurant')}</h4>
                    {lines.map((l) => (
                      <FolioRow key={l.id} line={l} onRemove={s.state === 'checked_in' && can('hotel.prices') ? () => setAsk({ remove: l }) : undefined} />
                    ))}
                  </>
                ) : s.state === 'checked_in' ? (
                  <p className="muted small">{t('htl.no_extras')}</p>
                ) : null}
                {s.state === 'checked_in' ? (
                  <dl className="totals">
                    <dt>{t('htl.c_nights')}</dt>
                    <dd>{money(dueTotal)}</dd>
                    <dt>{t('htl.extras')}</dt>
                    <dd>{money(extras)}</dd>
                    <dt className="grand">{t('htl.to_pay_today')}</dt>
                    <dd className="grand">{money(dueTotal + extras)}</dd>
                  </dl>
                ) : data.folio ? (
                  <dl className="totals">
                    <dt className="muted">{t('totals.vat_included')}</dt>
                    <dd className="muted">{money(data.folio.order.tax)}</dd>
                    <dt className="grand">{t('totals.total')}</dt>
                    <dd className="grand">{money(data.folio.order.total)}</dd>
                  </dl>
                ) : null}
              </div>
            )}
          </section>

          {data.history.length ? (
            <section className="item-moves">
              <h3>{t('htl.history')}</h3>
              <ol className="prot-history">
                {data.history.map((h, i) => (
                  <li key={i} className="prot-h">
                    <span className="prot-h-dot" aria-hidden="true" />
                    <span className="prot-h-body">
                      <span className="strong">{t(`htl.h_${h.kind}` as MessageKey)}</span>
                      {h.text && H_TEXT.has(h.kind) ? <span className="prot-h-text">{h.text}</span> : null}
                      <span className="muted small">{[dateTime(h.at), h.by_name].filter(Boolean).join(' · ')}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
      </Dialog>

      {ask === 'cancel' ? (
        <ReasonDialog
          title={t('htl.cancel_x', { name: stayName(s.number) })}
          label={t('htl.cancel_reason')}
          button={t('htl.cancel_stay')}
          danger
          onClose={() => setAsk(null)}
          onSave={async (reason) => {
            await action('hotel.cancel', { id: s.id, reason });
            toast(t('htl.cancelled_toast'));
            await done();
          }}
        />
      ) : null}
      {ask === 'no_show' ? (
        <Confirm
          title={t('htl.no_show')}
          body={t('htl.no_show_confirm', { name: data.guest.name, day: day(s.arrival) })}
          confirm={t('htl.no_show')}
          busy={busy}
          onClose={() => setAsk(null)}
          onConfirm={() => void run(async () => (await action('hotel.no_show', { id: s.id }), await done()), t('htl.no_show_toast'))}
        />
      ) : null}
      {ask === 'undo' ? (
        <Confirm
          title={t('htl.undo_check_in')}
          body={t('htl.undo_confirm', { room: data.room?.number ?? '' })}
          confirm={t('htl.undo_check_in')}
          busy={busy}
          onClose={() => setAsk(null)}
          onConfirm={() => void run(async () => (await action('hotel.undo_check_in', { id: s.id }), await done()), t('htl.undone_toast'))}
        />
      ) : null}
      {ask === 'check_in' ? <CheckInDialog detail={data} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'move' ? <MoveDialog detail={data} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'charge' ? <ChargeDialog stay={s} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask && typeof ask === 'object' && 'remove' in ask ? (
        <ReasonDialog
          title={t('htl.remove_x', { name: ask.remove.name })}
          label={t('htl.remove_reason')}
          button={t('htl.remove')}
          danger
          onClose={() => setAsk(null)}
          onSave={async (reason) => {
            await action('hotel.remove_charge', { line: ask.remove.id, reason });
            toast(t('htl.removed_toast'));
            await done();
          }}
        />
      ) : null}
      {ask && typeof ask === 'object' && 'pay' in ask ? (
        <PayDialog
          view={ask.pay}
          onClose={() => void done()}
          pay={(input) => action<OrderView & { change: number }>('hotel.check_out', { id: s.id, ...input })}
          next={{ label: t('htl.pay_done'), icon: 'check' }}
          onPaid={() => void done()}
        />
      ) : null}
    </>
  );
}

function NightsTable({ nights, title }: { nights: Night[]; title: string }) {
  // Runs of nights at the same price read as one line.
  const runs: { from: string; to: string; price: number; rate: string | null; count: number }[] = [];
  for (const n of nights) {
    const last = runs.at(-1);
    if (last && last.price === n.price && last.rate === n.rate && addDays(last.to, 1) === n.date) {
      last.to = n.date;
      last.count++;
    } else runs.push({ from: n.date, to: n.date, price: n.price, rate: n.rate, count: 1 });
  }
  return (
    <div className="htl-nights" aria-label={title}>
      {runs.map((r) => (
        <div key={r.from} className="htl-folio-row">
          <span>
            <span className="strong">{r.count === 1 ? day(r.from) : `${day(r.from)} – ${day(addDays(r.to, 1))}`}</span>
            <span className="muted small"> · {[tn('htl.nights', r.count), r.rate].filter(Boolean).join(' · ')}</span>
          </span>
          <span className="tabular">
            {r.count > 1 ? <span className="muted small">{r.count} × {money(r.price)} = </span> : null}
            {money(r.price * r.count)}
          </span>
        </div>
      ))}
    </div>
  );
}

function FolioRow({ line, onRemove }: { line: FolioLine; onRemove?: () => void }) {
  return (
    <div className="htl-folio-row">
      <span>
        <span className="strong">
          {fmtQty(line.qty)} × {line.name}
        </span>
        {line.note ? <span className="muted small"> · {line.note}</span> : null}
      </span>
      <span className="row-actions">
        <span className="tabular">{money(line.total)}</span>
        {onRemove ? <Button size="sm" variant="ghost" icon="trash" onClick={onRemove} aria-label={t('htl.remove_x', { name: line.name })} /> : null}
      </span>
    </div>
  );
}

function ReasonDialog({ title, label, button, danger, onClose, onSave }: { title: string; label: string; button: string; danger?: boolean; onClose: () => void; onSave: (reason: string) => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const short = reason.trim().length < 3;
  const save = async () => {
    setTried(true);
    if (short) return;
    if (await run(() => onSave(reason.trim()))) onClose();
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={save} busy={busy}>
            {button}
          </Button>
        </>
      }
    >
      <Field label={label} error={tried && short ? t('htl.reason_short') : null} required>
        <textarea className="input" rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Guests' details */

const emptyGuest = { name: '', phone: '', email: '', nationality: '', document_type: '' as DocType | '', document_no: '', birth_date: '', address: '' };
type GuestDraft = typeof emptyGuest;
const fromGuest = (g: Guest): GuestDraft => ({
  name: g.name,
  phone: g.phone ?? '',
  email: g.email ?? '',
  nationality: g.nationality ?? '',
  document_type: g.document_type ?? '',
  document_no: g.document_no ?? '',
  birth_date: g.birth_date ?? '',
  address: g.address ?? '',
});
const guestPayload = (g: GuestDraft) => ({
  name: g.name.trim(),
  phone: g.phone.trim() || null,
  email: g.email.trim() || null,
  nationality: g.nationality.trim().toUpperCase() || null,
  document_type: g.document_type || null,
  document_no: g.document_no.trim() || null,
  birth_date: g.birth_date || null,
  address: g.address.trim() || null,
});
function guestErrors(g: GuestDraft, needDocument: boolean) {
  return {
    name: g.name.trim() ? null : t('form.required'),
    nationality: g.nationality.trim() && !/^[A-Za-z]{2}$/.test(g.nationality.trim()) ? t('htl.nationality_wrong') : null,
    document_type: needDocument && !g.document_type ? t('form.required') : g.document_no.trim() && !g.document_type ? t('htl.doc_type_needed') : null,
    document_no: needDocument && !g.document_no.trim() ? t('form.required') : null,
    email: g.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(g.email.trim()) ? t('htl.email_wrong') : null,
  };
}

function GuestFields({ value, onChange, errors, document: withDoc, contact = true, autoFocus }: { value: GuestDraft; onChange: (g: GuestDraft) => void; errors: ReturnType<typeof guestErrors> | null; document: boolean; contact?: boolean; autoFocus?: boolean }) {
  const set = (patch: Partial<GuestDraft>) => onChange({ ...value, ...patch });
  return (
    <div className="form-grid">
      <Field label={t('htl.guest_name')} error={errors?.name} required>
        <input className="input" maxLength={120} value={value.name} onChange={(e) => set({ name: e.target.value })} autoComplete="off" data-autofocus={autoFocus || undefined} />
      </Field>
      <Field label={t('htl.nationality')} hint={t('htl.nationality_hint')} error={errors?.nationality}>
        <input className="input htl-cc" maxLength={2} value={value.nationality} onChange={(e) => set({ nationality: e.target.value.toUpperCase() })} placeholder="AL" />
      </Field>
      {contact ? (
        <>
          <Field label={t('htl.phone')}>
            <input className="input" type="tel" maxLength={40} value={value.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
          <Field label={t('htl.email')} error={errors?.email}>
            <input className="input" type="email" maxLength={120} value={value.email} onChange={(e) => set({ email: e.target.value })} />
          </Field>
        </>
      ) : null}
      {withDoc ? (
        <>
          <Field label={t('htl.document')} error={errors?.document_type} required>
            <select className="input" value={value.document_type} onChange={(e) => set({ document_type: e.target.value as DocType })}>
              <option value="">{t('htl.choose')}</option>
              {DOCS.map((d) => (
                <option key={d} value={d}>
                  {docLabel(d)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('htl.document_no')} error={errors?.document_no} required>
            <input className="input tabular" maxLength={40} value={value.document_no} onChange={(e) => set({ document_no: e.target.value })} autoComplete="off" />
          </Field>
          <Field label={t('htl.birth_date')}>
            <input className="input" type="date" value={value.birth_date} onChange={(e) => set({ birth_date: e.target.value })} />
          </Field>
          <Field label={t('htl.address')}>
            <input className="input" maxLength={200} value={value.address} onChange={(e) => set({ address: e.target.value })} />
          </Field>
        </>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- Booking */

function BookingForm({ stay, room, arrival, onClose, onDone }: { stay?: Stay; room?: string; arrival?: string; onClose: () => void; onDone: (id: string) => Promise<void> }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const { toast } = useUi();
  const { data: desk } = useAction<{ today: string }>('hotel.front_desk');
  const { data: setup } = useAction<Setup>('hotel.setup');
  const inRoom = stay?.state === 'checked_in';
  const [from, setFrom] = useState(stay?.arrival ?? arrival ?? '');
  const [to, setTo] = useState(stay?.departure ?? (arrival ? addDays(arrival, 1) : ''));
  const [adults, setAdults] = useState(stay?.adults ?? 2);
  const [children, setChildren] = useState(stay?.children ?? 0);
  const [type, setType] = useState(stay?.room_type ?? '');
  const [roomId, setRoomId] = useState(stay?.room ?? room ?? '');
  const [guestId, setGuestId] = useState<string | null>(stay?.guest ?? null);
  const [guestName, setGuestName] = useState('');
  const [newGuest, setNewGuest] = useState<GuestDraft>(emptyGuest);
  const [searching, setSearching] = useState('');
  const [found, setFound] = useState<GuestRow[] | null>(null);
  const [source, setSource] = useState<Source>(stay?.source ?? 'direct');
  const [agency, setAgency] = useState(stay?.agency ?? '');
  const [reference, setReference] = useState(stay?.reference ?? '');
  const [agreed, setAgreed] = useState(stay?.agreed_price !== null && stay?.agreed_price !== undefined ? lek(stay.agreed_price) || '0' : '');
  const [note, setNote] = useState(stay?.note ?? '');
  const [avail, setAvail] = useState<Availability | null>(null);
  const [tried, setTried] = useState(false);
  const { fail } = useUi();

  // Today and tomorrow as soon as the date of the business is known.
  useEffect(() => {
    if (desk && !from) {
      setFrom(desk.today);
      setTo(addDays(desk.today, 1));
    }
  }, [desk, from]);
  // A room picked on the calendar sets its type.
  useEffect(() => {
    if (setup && roomId && !type) setType(setup.rooms.find((r) => r.id === roomId)?.type ?? '');
  }, [setup, roomId, type]);
  useEffect(() => {
    if (!from || !to || to <= from) return setAvail(null);
    const h = setTimeout(() => action<Availability>('hotel.availability', { arrival: from, departure: to, except: stay?.id }).then(setAvail, fail), 150);
    return () => clearTimeout(h);
  }, [from, to, stay?.id, fail]);
  useEffect(() => {
    const q = searching.trim();
    if (q.length < 2 || !can('hotel.guests')) return setFound(null);
    const h = setTimeout(() => action<GuestRow[]>('hotel.guests', { q }).then(setFound, fail), 250);
    return () => clearTimeout(h);
  }, [searching, can, fail]);
  useEffect(() => {
    if (stay && can('hotel.guests')) action<{ guest: Guest }>('hotel.guest', { id: stay.guest }).then((g) => setGuestName(g.guest.name), fail);
  }, [stay, can, fail]);

  const nights = from && to ? between(from, to) : 0;
  const chosen = avail?.types.find((x) => x.type.id === type);
  const agreedMinor = agreed.trim() ? parseMoney(agreed) : null;
  const total = agreedMinor !== null ? agreedMinor * nights : (chosen?.total ?? 0);
  const roomsFree = chosen ? chosen.free_rooms.filter((r) => !r.out_of_order || r.id === roomId) : [];
  const g = guestErrors(newGuest, false);
  const errors = {
    dates: !from || !to ? t('form.required') : to <= from ? t('htl.departure_after') : nights > 90 ? t('htl.too_long') : !stay && desk && from < desk.today ? t('htl.arrival_past') : null,
    type: !type ? t('htl.choose_type') : chosen && chosen.free <= 0 && !(stay && stay.room_type === type) ? t('htl.type_full') : null,
    people: chosen && adults + children > chosen.type.capacity ? tn('htl.too_many_people', chosen.type.capacity) : null,
    guest: !guestId && Object.values(g).some(Boolean) ? true : null,
    agreed: agreed.trim() && (agreedMinor === null || agreedMinor < 0) ? t('htl.amount_wrong') : null,
  };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    let id = stay?.id ?? '';
    const ok = await run(async () => {
      const r = await action<Stay>('hotel.book', {
        id: stay?.id,
        guest: guestId,
        new_guest: guestId ? null : guestPayload(newGuest),
        room_type: type,
        room: roomId || null,
        arrival: from,
        departure: to,
        adults,
        children,
        source,
        agency: source === 'agency' ? agency.trim() || null : null,
        reference: reference.trim() || null,
        agreed_price: agreedMinor,
        note: note.trim() || null,
      });
      id = r.id;
      toast(stay ? t('htl.saved') : t('htl.booked_as', { name: stayName(r.number) }));
    });
    if (ok) await onDone(id);
  };
  return (
    <Dialog
      title={stay ? t('htl.edit_x', { name: stayName(stay.number) }) : t('htl.new_stay')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="muted tabular">{nights > 0 && type ? t('htl.total_for', { n: tn('htl.nights', nights), amount: money(total) }) : null}</span>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon={stay ? 'check' : 'plus'} onClick={save} busy={busy}>
            {stay ? t('htl.save') : t('htl.book')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {inRoom ? <p className="callout"><Icon name="bed" size={16} /><span>{t('htl.in_room_edit_note')}</span></p> : null}
        <div className="form-grid htl-dates">
          <Field label={t('htl.arrival')} error={tried ? errors.dates : null} required>
            <input
              className="input"
              type="date"
              value={from}
              disabled={inRoom}
              onChange={(e) => {
                const v = e.target.value;
                setFrom(v);
                if (v && (!to || to <= v)) setTo(addDays(v, Math.max(1, nights)));
              }}
            />
          </Field>
          <Field label={t('htl.departure')} hint={nights > 0 ? tn('htl.nights', nights) : undefined} required>
            <input className="input" type="date" value={to} min={from ? addDays(from, 1) : undefined} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label={t('htl.adults_label')} error={tried ? errors.people : null}>
            <input className="input tabular" type="number" min={1} max={20} value={adults} onChange={(e) => setAdults(Math.max(1, Number(e.target.value) || 1))} />
          </Field>
          <Field label={t('htl.children_label')}>
            <input className="input tabular" type="number" min={0} max={20} value={children} onChange={(e) => setChildren(Math.max(0, Number(e.target.value) || 0))} />
          </Field>
        </div>

        <fieldset className="htl-types" disabled={inRoom}>
          <legend className="field-label">{t('htl.room_type')}</legend>
          {!avail ? (
            <Skeleton h={90} />
          ) : !avail.types.length ? (
            <p className="muted">{t('htl.no_types')}</p>
          ) : (
            <div className="htl-type-cards">
              {avail.types.map((x) => {
                const full = x.free <= 0 && !(stay && stay.room_type === x.type.id);
                const small = adults + children > x.type.capacity;
                return (
                  <label key={x.type.id} className={`htl-type-card${type === x.type.id ? ' active' : ''}${full || small ? ' off' : ''}`}>
                    <input
                      type="radio"
                      name="room-type"
                      checked={type === x.type.id}
                      onChange={() => {
                        setType(x.type.id);
                        if (roomId && !x.free_rooms.some((r) => r.id === roomId)) setRoomId('');
                      }}
                    />
                    <span className="strong">{x.type.name}</span>
                    <span className="muted small">{[tn('htl.sleeps', x.type.capacity), x.type.beds].filter(Boolean).join(' · ')}</span>
                    <span className="tabular strong">{money(x.total)}</span>
                    <span className={`small ${full ? 'text-danger' : x.free <= 1 ? 'text-warn' : 'muted'}`}>{full ? t('htl.full') : tn('htl.n_free', x.free)}</span>
                  </label>
                );
              })}
            </div>
          )}
          {tried && errors.type ? (
            <span className="field-msg" role="alert">
              <Icon name="alert" size={14} /> {errors.type}
            </span>
          ) : null}
        </fieldset>

        <div className="form-grid">
          <Field label={t('htl.room')} hint={inRoom ? t('htl.move_hint') : t('htl.room_hint')}>
            <select className="input" value={roomId} onChange={(e) => setRoomId(e.target.value)} disabled={!type || inRoom}>
              <option value="">{t('htl.room_later')}</option>
              {(inRoom && stay?.room ? (setup?.rooms ?? []).filter((r) => r.id === stay.room) : roomsFree).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.number}
                  {r.floor ? ` · ${t('htl.floor_n', { n: r.floor })}` : ''}
                  {r.status === 'dirty' ? ` · ${t('htl.rs_dirty')}` : ''}
                </option>
              ))}
            </select>
          </Field>
          {can('hotel.prices') ? (
            <Field label={t('htl.agreed_price')} hint={t('htl.agreed_hint')} error={tried ? errors.agreed : null}>
              <input className="input tabular" inputMode="decimal" value={agreed} onChange={(e) => setAgreed(e.target.value)} placeholder={chosen ? lek(Math.round(chosen.total / Math.max(1, nights))) : ''} disabled={inRoom} />
            </Field>
          ) : null}
        </div>

        <section className="htl-guest-box">
          <h3 className="field-label">{t('htl.guest')}</h3>
          {guestId ? (
            <div className="list-row compact-move">
              <span className="list-icon" aria-hidden="true">
                <Icon name="star" size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">{guestName || t('htl.guest_chosen')}</span>
              </span>
              {!inRoom ? (
                <Button size="sm" variant="ghost" onClick={() => (setGuestId(null), setGuestName(''))}>
                  {t('htl.change')}
                </Button>
              ) : null}
            </div>
          ) : (
            <>
              {can('hotel.guests') ? (
                <label className="search htl-guest-search">
                  <Icon name="search" size={16} />
                  <input className="input" type="search" value={searching} onChange={(e) => setSearching(e.target.value)} placeholder={t('htl.find_guest')} aria-label={t('htl.find_guest')} />
                </label>
              ) : null}
              {found?.length ? (
                <div className="list-card">
                  {found.slice(0, 6).map((x) => (
                    <button key={x.id} className="list-row compact-move" onClick={() => (setGuestId(x.id), setGuestName(x.name), setSearching(''))}>
                      <span className="list-main">
                        <span className="list-title">{x.name}</span>
                        <span className="list-sub">{[x.nationality, x.document_no, x.phone, x.stays ? tn('htl.n_stays', x.stays) : null].filter(Boolean).join(' · ')}</span>
                      </span>
                      <Icon name="chevronRight" size={18} />
                    </button>
                  ))}
                </div>
              ) : found && searching.trim().length >= 2 ? (
                <p className="muted small">{t('htl.no_guest_found')}</p>
              ) : null}
              <p className="muted small">{t('htl.new_guest_hint')}</p>
              <GuestFields value={newGuest} onChange={setNewGuest} errors={tried ? g : null} document={false} />
            </>
          )}
        </section>

        <div className="form-grid">
          <Field label={t('htl.source')}>
            <select className="input" value={source} onChange={(e) => setSource(e.target.value as Source)}>
              {SOURCES.map((x) => (
                <option key={x} value={x}>
                  {t(`htl.src_${x}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          {source === 'agency' ? (
            <Field label={t('htl.agency')} hint={t('htl.agency_hint')}>
              <input className="input" maxLength={80} value={agency} onChange={(e) => setAgency(e.target.value)} list="htl-agencies" />
            </Field>
          ) : null}
          <Field label={t('htl.reference')} hint={t('htl.reference_hint')}>
            <input className="input tabular" maxLength={60} value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        </div>
        <datalist id="htl-agencies">
          {['Booking.com', 'Expedia', 'Airbnb'].map((a) => (
            <option key={a} value={a} />
          ))}
        </datalist>
        <Field label={t('htl.note')}>
          <textarea className="input" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Check-in, move, extras */

function CheckInDialog({ detail, onClose, onDone }: { detail: StayDetail; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { fail } = useUi();
  const s = detail.stay;
  const [avail, setAvail] = useState<Availability | null>(null);
  const [roomId, setRoomId] = useState(s.room ?? '');
  const [main, setMain] = useState<GuestDraft>(fromGuest(detail.guest));
  const [others, setOthers] = useState<GuestDraft[]>(() => detail.guests.map(fromGuest));
  const [otherIds] = useState<string[]>(() => detail.guests.map((g) => g.id));
  const [tried, setTried] = useState(false);
  const [dirty, setDirty] = useState(false);
  const arrival = s.arrival < detail.today ? detail.today : s.arrival;
  useEffect(() => {
    action<Availability>('hotel.availability', { arrival, departure: s.departure, except: s.id }).then(setAvail, fail);
  }, [arrival, s.departure, s.id, fail]);
  const rooms = avail?.types.find((x) => x.type.id === s.room_type)?.free_rooms.filter((r) => !r.out_of_order) ?? [];
  const room = rooms.find((r) => r.id === roomId);
  const max = s.adults + s.children;
  const mainErr = guestErrors(main, true);
  const otherErr = others.map((o) => guestErrors(o, true));
  const bad = !roomId || Object.values(mainErr).some(Boolean) || otherErr.some((e) => Object.values(e).some(Boolean));
  const save = async (dirtyOk = false) => {
    setTried(true);
    if (bad) return;
    if (room?.status === 'dirty' && !dirtyOk) return setDirty(true);
    const ok = await run(async () => {
      await action('hotel.save_guest', { id: detail.guest.id, ...guestPayload(main) });
      const ids: string[] = [];
      for (const [i, o] of others.entries()) ids.push((await action<Guest>('hotel.save_guest', { id: otherIds[i] ?? null, ...guestPayload(o) })).id);
      await action('hotel.check_in', { id: s.id, room: roomId, guests: ids, dirty_ok: dirtyOk });
    }, t('htl.checked_in_toast', { room: room?.number ?? '' }));
    if (ok) await onDone();
  };
  if (dirty && room) {
    return (
      <Confirm
        title={t('htl.room_dirty_title', { room: room.number })}
        body={t('htl.room_dirty_body')}
        confirm={t('htl.check_in_anyway')}
        busy={busy}
        onClose={() => setDirty(false)}
        onConfirm={() => void save(true)}
      />
    );
  }
  return (
    <Dialog
      title={t('htl.check_in_x', { name: detail.guest.name })}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="key" onClick={() => void save()} busy={busy}>
            {t('htl.check_in')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {s.arrival < detail.today ? (
          <p className="callout callout-warn">
            <Icon name="clock" size={16} />
            <span>{t('htl.late_check_in_note', { day: day(s.arrival) })}</span>
          </p>
        ) : null}
        <Field label={t('htl.room')} error={tried && !roomId ? t('htl.choose_room') : null} required>
          {!avail ? (
            <Skeleton h={48} />
          ) : rooms.length ? (
            <div className="htl-room-pick" role="radiogroup">
              {rooms.map((r) => (
                <button key={r.id} type="button" role="radio" aria-checked={roomId === r.id} className={`htl-room-opt${roomId === r.id ? ' active' : ''} rs-${r.status}`} onClick={() => setRoomId(r.id)}>
                  <strong>{r.number}</strong>
                  <span className="small">{t(`htl.rs_${r.status}` as MessageKey)}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-danger">{t('htl.no_room_free')}</p>
          )}
        </Field>
        <section className="htl-guest-box">
          <h3 className="field-label">{t('htl.main_guest')}</h3>
          <p className="muted small">{t('htl.register_hint')}</p>
          <GuestFields value={main} onChange={setMain} errors={tried ? mainErr : null} document />
        </section>
        {others.map((o, i) => (
          <section key={i} className="htl-guest-box">
            <div className="prot-history-head">
              <h3 className="field-label">{t('htl.guest_n', { n: i + 2 })}</h3>
              {!otherIds[i] ? (
                <Button size="sm" variant="ghost" icon="trash" onClick={() => setOthers(others.filter((_, j) => j !== i))}>
                  {t('htl.remove')}
                </Button>
              ) : null}
            </div>
            <GuestFields value={o} onChange={(v) => setOthers(others.map((x, j) => (j === i ? v : x)))} errors={tried ? otherErr[i]! : null} document contact={false} autoFocus={!otherIds[i]} />
          </section>
        ))}
        {others.length + 1 < max ? (
          <Button icon="plus" onClick={() => setOthers([...others, { ...emptyGuest, nationality: main.nationality }])}>
            {t('htl.add_guest', { n: others.length + 1, of: max })}
          </Button>
        ) : null}
      </div>
    </Dialog>
  );
}

function MoveDialog({ detail, onClose, onDone }: { detail: StayDetail; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { fail } = useUi();
  const s = detail.stay;
  const [avail, setAvail] = useState<Availability | null>(null);
  const [roomId, setRoomId] = useState('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  useEffect(() => {
    action<Availability>('hotel.availability', { arrival: detail.today < s.departure ? detail.today : addDays(s.departure, -1), departure: s.departure, except: s.id }).then(setAvail, fail);
  }, [detail.today, s.departure, s.id, fail]);
  const options = (avail?.types ?? []).flatMap((x) => x.free_rooms.filter((r) => !r.out_of_order && r.id !== s.room).map((r) => ({ ...r, type: x.type, total: x.total })));
  const save = async () => {
    setTried(true);
    if (!roomId || reason.trim().length < 3) return;
    const room = options.find((r) => r.id === roomId);
    if (await run(() => action('hotel.move', { id: s.id, room: roomId, reason: reason.trim() }), t('htl.moved_toast', { room: room?.number ?? '' }))) await onDone();
  };
  return (
    <Dialog
      title={t('htl.move_x', { name: detail.guest.name })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="move" onClick={save} busy={busy}>
            {t('htl.move')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('htl.move_note', { room: detail.room?.number ?? '' })}</p>
        <Field label={t('htl.new_room')} error={tried && !roomId ? t('htl.choose_room') : null} required>
          {!avail ? (
            <Skeleton h={48} />
          ) : options.length ? (
            <div className="htl-room-pick" role="radiogroup">
              {options.map((r) => (
                <button key={r.id} type="button" role="radio" aria-checked={roomId === r.id} className={`htl-room-opt${roomId === r.id ? ' active' : ''} rs-${r.status}`} onClick={() => setRoomId(r.id)}>
                  <strong>{r.number}</strong>
                  <span className="small">{r.type.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-danger">{t('htl.no_room_free')}</p>
          )}
        </Field>
        <Field label={t('htl.move_reason')} error={tried && reason.trim().length < 3 ? t('htl.reason_short') : null} required>
          <input className="input" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function ChargeDialog({ stay, onClose, onDone }: { stay: Stay; onClose: () => void; onDone: () => Promise<void> }) {
  const { data: options } = useAction<{ id: string; name: string; price: number }[]>('hotel.charge_options');
  const { busy, run } = useSave();
  const [q, setQ] = useState('');
  const [pick, setPick] = useState<{ id: string; name: string; price: number } | null>(null);
  const [count, setCount] = useState(1);
  const [note, setNote] = useState('');
  const list = (options ?? []).filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()));
  const save = async () => {
    if (!pick) return;
    if (await run(() => action('hotel.add_charge', { stay: stay.id, product: pick.id, qty: count, note: note.trim() || null }), t('htl.charged_toast', { name: pick.name }))) await onDone();
  };
  return (
    <Dialog
      title={t('htl.add_extra')}
      onClose={onClose}
      footer={
        pick ? (
          <>
            <span className="muted tabular">{money(pick.price * count)}</span>
            <Button variant="ghost" onClick={() => setPick(null)}>
              {t('htl.back')}
            </Button>
            <Button variant="primary" icon="plus" onClick={save} busy={busy}>
              {t('htl.add_to_bill')}
            </Button>
          </>
        ) : undefined
      }
    >
      {pick ? (
        <div className="form-stack">
          <p className="strong">{pick.name}</p>
          <div className="form-grid">
            <Field label={t('common.quantity')}>
              <input className="input tabular" type="number" min={1} max={999} value={count} onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))} data-autofocus />
            </Field>
            <Field label={t('htl.note')} hint={t('htl.extra_note_hint')}>
              <input className="input" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
        </div>
      ) : (
        <div className="form-stack">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('htl.find_item')} aria-label={t('htl.find_item')} data-autofocus />
          </label>
          {!options ? (
            <Skeleton h={200} />
          ) : !list.length ? (
            <EmptyState icon="search" title={t('htl.no_items')} />
          ) : (
            <div className="list-card htl-pick-list">
              {list.slice(0, 60).map((p) => (
                <button key={p.id} className="list-row compact-move" onClick={() => setPick(p)}>
                  <span className="list-main">
                    <span className="list-title">{p.name}</span>
                  </span>
                  <span className="tabular">{money(p.price)}</span>
                  <Icon name="chevronRight" size={18} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Housekeeping */

function Housekeeping() {
  const { can } = useSession();
  const { data, reload } = useAction<HkRoom[]>('hotel.housekeeping');
  const { busy, run } = useSave();
  const [filter, setFilter] = useState<'todo' | 'all'>('todo');
  const [broken, setBroken] = useState<HkRoom | null>(null);
  if (!data) return <Skeleton h={320} r="var(--radius-lg)" />;
  const todo = data.filter((r) => r.task && r.task !== 'out_of_order');
  const shown = filter === 'todo' ? data.filter((r) => r.task || r.arriving_today) : data;
  const mark = (r: HkRoom, status: RoomStatus) => void run(async () => (await action('hotel.set_room_status', { room: r.id, status }), await reload()), t(`htl.marked_${status}` as MessageKey, { room: r.number }));
  const back = (r: HkRoom) => void run(async () => (await action('hotel.set_out_of_order', { room: r.id, out: false }), await reload()), t('htl.back_in_order', { room: r.number }));
  return (
    <>
      <div className="toolbar">
        <div className="segmented tabs" role="tablist" aria-label={t('htl.show')}>
          <button role="tab" aria-selected={filter === 'todo'} className={filter === 'todo' ? 'active' : ''} onClick={() => setFilter('todo')}>
            {t('htl.hk_todo')} {todo.length ? <span className="count">{todo.length}</span> : null}
          </button>
          <button role="tab" aria-selected={filter === 'all'} className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>
            {t('htl.hk_all')}
          </button>
        </div>
        <span className="muted small">{t('htl.hk_hint')}</span>
      </div>
      {!shown.length ? (
        <EmptyState icon="sparkle" title={t('htl.hk_done')} body={t('htl.hk_done_body')} />
      ) : (
        <div className="htl-hk-grid">
          {shown.map((r) => (
            <article key={r.id} className={`htl-hk card rs-${r.out_of_order ? 'ooo' : r.status}`}>
              <header>
                <strong className="htl-hk-no">{r.number}</strong>
                <RoomBadge r={r} />
              </header>
              <p className="muted small">{[r.type_name, r.floor ? t('htl.floor_n', { n: r.floor }) : null].filter(Boolean).join(' · ')}</p>
              <p className="htl-hk-task">
                {r.out_of_order ? (
                  <>
                    <Icon name="alert" size={16} /> {r.ooo_reason}
                  </>
                ) : r.task === 'departure' ? (
                  <>
                    <Icon name="logout" size={16} /> {t('htl.task_departure')}
                  </>
                ) : r.task === 'service' ? (
                  <>
                    <Icon name="bed" size={16} /> {r.leaving_today ? t('htl.task_service_leaving') : t('htl.task_service')}
                  </>
                ) : r.occupied ? (
                  <>
                    <Icon name="checkCircle" size={16} /> {t('htl.task_none_occupied')}
                  </>
                ) : (
                  <>
                    <Icon name="checkCircle" size={16} /> {t('htl.task_ready')}
                  </>
                )}
              </p>
              <p className="small">
                {r.arriving_today ? (
                  <span className="htl-flag">
                    <Icon name="key" size={14} /> {t('htl.arriving_today', { name: r.guest ?? '' })}
                  </span>
                ) : r.occupied ? (
                  <span className="muted">{[r.guest, tn('htl.people_n', r.people)].join(' · ')}</span>
                ) : null}
                {r.cleaned_at && r.status !== 'dirty' ? <span className="muted"> {t('htl.cleaned_at', { at: dateTime(r.cleaned_at), by: r.cleaned_by_name ?? '' })}</span> : null}
              </p>
              {can('hotel.housekeeping') ? (
                <footer className="row-actions">
                  {r.out_of_order ? (
                    <Button size="sm" icon="check" onClick={() => back(r)} busy={busy}>
                      {t('htl.back_to_use')}
                    </Button>
                  ) : (
                    <>
                      {r.status === 'dirty' || r.task === 'service' ? (
                        <Button size="sm" variant="primary" icon="sparkle" onClick={() => mark(r, 'clean')} busy={busy}>
                          {t('htl.mark_clean')}
                        </Button>
                      ) : null}
                      {r.status === 'clean' && can('hotel.inspect') && r.task !== 'service' ? (
                        <Button size="sm" icon="shieldCheck" onClick={() => mark(r, 'inspected')} busy={busy}>
                          {t('htl.mark_inspected')}
                        </Button>
                      ) : null}
                      {r.status !== 'dirty' ? (
                        <Button size="sm" variant="ghost" onClick={() => mark(r, 'dirty')} busy={busy}>
                          {t('htl.mark_dirty')}
                        </Button>
                      ) : null}
                      {!r.occupied ? (
                        <Button size="sm" variant="ghost" icon="alert" onClick={() => setBroken(r)}>
                          {t('htl.out_of_order')}
                        </Button>
                      ) : null}
                    </>
                  )}
                </footer>
              ) : null}
            </article>
          ))}
        </div>
      )}
      {broken ? (
        <ReasonDialog
          title={t('htl.ooo_x', { room: broken.number })}
          label={t('htl.ooo_reason')}
          button={t('htl.out_of_order')}
          onClose={() => setBroken(null)}
          onSave={async (reason) => {
            await action('hotel.set_out_of_order', { room: broken.id, out: true, reason });
            await reload();
          }}
        />
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------- Guests and the register */

function Guests({ onOpen }: { onOpen: (id: string) => void }) {
  const [view, setView] = useState<'guests' | 'register'>('guests');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  const { data } = useAction<GuestRow[]>('hotel.guests', { q: query });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <div className="toolbar">
        <div className="segmented tabs" role="tablist" aria-label={t('htl.show')}>
          {(['guests', 'register'] as const).map((v) => (
            <button key={v} role="tab" aria-selected={view === v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
              {t(`htl.gv_${v}` as MessageKey)}
            </button>
          ))}
        </div>
        {view === 'guests' ? (
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('htl.search_guests')} aria-label={t('htl.search_guests')} />
          </label>
        ) : null}
      </div>
      {view === 'register' ? (
        <Register />
      ) : !data ? (
        <Skeleton h={300} r="var(--radius-lg)" />
      ) : !data.length ? (
        <EmptyState icon="users" title={query ? t('htl.none_found') : t('htl.no_guests')} />
      ) : (
        <Table
          view="hotel.guests"
          head={[t('htl.c_guest'), t('htl.nationality'), t('htl.document'), t('htl.contact'), t('htl.c_stays'), t('htl.last_stay')]}
          phoneHide={[1, 2, 3]}
          left={[0, 1, 2, 3]}
          rows={data.map((g) => ({
            key: g.id,
            onClick: () => setOpen(g.id),
            cells: [
              <span className="strong">{g.name}</span>,
              <span>{g.nationality ?? '—'}</span>,
              <span className="tabular">{g.document_no ? `${docLabel(g.document_type)} ${g.document_no}` : '—'}</span>,
              <span className="muted">{[g.phone, g.email].filter(Boolean).join(' · ') || '—'}</span>,
              <span className="tabular">{g.stays || '—'}</span>,
              <span className="tabular">{g.last_stay ? day(g.last_stay) : '—'}</span>,
            ],
          }))}
        />
      )}
      {open ? <GuestDialog id={open} onClose={() => setOpen(null)} onOpenStay={(id) => (setOpen(null), onOpen(id))} /> : null}
    </>
  );
}

function GuestDialog({ id, onClose, onOpenStay }: { id: string; onClose: () => void; onOpenStay: (id: string) => void }) {
  const { can } = useSession();
  const { data, reload } = useAction<{ guest: Guest; stays: { id: string; number: number; state: StayState; arrival: string; departure: string; total: number; room_type: string; room: string | null }[] }>('hotel.guest', { id });
  const [draft, setDraft] = useState<GuestDraft | null>(null);
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  if (!data) {
    return (
      <Dialog title={t('htl.guest')} onClose={onClose}>
        <Skeleton h={200} />
      </Dialog>
    );
  }
  const g = data.guest;
  const errors = draft ? guestErrors(draft, false) : null;
  const save = async () => {
    setTried(true);
    if (!draft || Object.values(errors!).some(Boolean)) return;
    if (await run(() => action('hotel.save_guest', { id: g.id, ...guestPayload(draft), note: g.note }), t('htl.saved'))) {
      setDraft(null);
      await reload();
    }
  };
  return (
    <Dialog
      title={g.name}
      onClose={onClose}
      size="lg"
      footer={
        can('hotel.book') ? (
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" icon="check" onClick={save} busy={busy}>
                {t('htl.save')}
              </Button>
            </>
          ) : (
            <Button icon="edit" onClick={() => setDraft(fromGuest(g))}>
              {t('htl.edit')}
            </Button>
          )
        ) : undefined
      }
    >
      <div className="form-stack">
        {draft ? (
          <GuestFields value={draft} onChange={setDraft} errors={tried ? errors : null} document={false} />
        ) : (
          <dl className="appr-facts">
            <dt>{t('htl.nationality')}</dt>
            <dd>{g.nationality ?? '—'}</dd>
            <dt>{t('htl.document')}</dt>
            <dd>{g.document_no ? `${docLabel(g.document_type)} ${g.document_no}` : '—'}</dd>
            <dt>{t('htl.birth_date')}</dt>
            <dd>{g.birth_date ? day(g.birth_date) : '—'}</dd>
            <dt>{t('htl.contact')}</dt>
            <dd>{[g.phone, g.email].filter(Boolean).join(' · ') || '—'}</dd>
            {g.address ? (
              <>
                <dt>{t('htl.address')}</dt>
                <dd>{g.address}</dd>
              </>
            ) : null}
          </dl>
        )}
        {draft ? <GuestFields value={draft} onChange={setDraft} errors={tried ? errors : null} document contact={false} /> : null}
        <section className="item-moves">
          <h3>{t('htl.stays_of_guest')}</h3>
          {data.stays.length ? (
            <div className="list-card">
              {data.stays.map((s) => (
                <button key={s.id} className="list-row compact-move" onClick={() => onOpenStay(s.id)}>
                  <span className="list-main">
                    <span className="list-title">
                      {stayName(s.number)} · {day(s.arrival)} – {day(s.departure)}
                    </span>
                    <span className="list-sub">{[s.room ? `${s.room} · ${s.room_type}` : s.room_type, money(s.total)].join(' · ')}</span>
                  </span>
                  <StateBadge s={s.state} />
                  <Icon name="chevronRight" size={18} />
                </button>
              ))}
            </div>
          ) : (
            <p className="muted">{t('htl.no_stays_yet')}</p>
          )}
        </section>
      </div>
    </Dialog>
  );
}

function Register() {
  const { data: desk } = useAction<{ today: string }>('hotel.front_desk');
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const start = from ?? (desk ? `${desk.today.slice(0, 8)}01` : null);
  const end = to ?? desk?.today ?? null;
  const [rows, setRows] = useState<RegisterRow[] | null>(null);
  const { fail } = useUi();
  useEffect(() => {
    if (!start || !end || end < start) return;
    action<RegisterRow[]>('hotel.register', { from: start, to: end }).then(setRows, fail);
  }, [start, end, fail]);
  const head = [t('htl.c_no'), t('htl.arrival'), t('htl.departure'), t('htl.c_room'), t('htl.c_guest'), t('htl.nationality'), t('htl.document'), t('htl.document_no'), t('htl.birth_date'), t('htl.address')];
  const exportCsv = () =>
    rows &&
    downloadCsv(
      `guest-register-${start}-${end}.csv`,
      head,
      rows.map((r) => [stayName(r.number), r.arrival, r.departure, r.room ?? '', r.name, r.nationality ?? '', docLabel(r.document_type), r.document_no ?? '', r.birth_date ?? '', r.address ?? '']),
    );
  return (
    <section className="panel">
      <div className="toolbar">
        <span className="row-actions">
          <Field label={t('htl.from_day')}>
            <input className="input" type="date" value={start ?? ''} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t('htl.to_day')}>
            <input className="input" type="date" value={end ?? ''} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </span>
        <span className="row-actions">
          <Button icon="printer" onClick={() => window.print()} disabled={!rows?.length}>
            {t('htl.print')}
          </Button>
          <Button icon="download" onClick={exportCsv} disabled={!rows?.length}>
            {t('htl.export')}
          </Button>
        </span>
      </div>
      <p className="muted small">{t('htl.register_note')}</p>
      {!rows ? (
        <Skeleton h={240} />
      ) : !rows.length ? (
        <EmptyState icon="book" title={t('htl.register_empty')} />
      ) : (
        <Table
          view="hotel.register"
          head={[t('htl.c_no'), t('htl.c_dates'), t('htl.c_room'), t('htl.c_guest'), t('htl.nationality'), t('htl.document'), t('htl.birth_date')]}
          phoneHide={[0, 2, 4, 6]}
          left={[0, 1, 2, 3, 4, 5]}
          rows={rows.map((r, i) => ({
            key: `${r.number}-${i}`,
            cells: [
              <span className="tabular">{stayName(r.number)}</span>,
              <span className="tabular">
                {day(r.arrival)} – {day(r.departure)}
              </span>,
              <span>{r.room ?? '—'}</span>,
              <span className={r.main ? 'strong' : ''}>{r.name}</span>,
              <span>{r.nationality ?? '—'}</span>,
              <span className="tabular">{r.document_no ? `${docLabel(r.document_type)} ${r.document_no}` : '—'}</span>,
              <span className="tabular">{r.birth_date ? day(r.birth_date) : '—'}</span>,
            ],
          }))}
        />
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- Reports */

function Reports() {
  const { data: desk } = useAction<{ today: string }>('hotel.front_desk');
  const [range, setRange] = useState<'month' | 'last' | 'next' | 'year'>('month');
  const today = desk?.today ?? null;
  const span = useMemo(() => {
    if (!today) return null;
    const first = `${today.slice(0, 8)}01`;
    const monthEnd = (d: string) => addDays(`${addDays(d, 32).slice(0, 8)}01`, -1);
    if (range === 'last') {
      const prev = `${addDays(first, -1).slice(0, 8)}01`;
      return { from: prev, to: addDays(first, -1) };
    }
    if (range === 'next') return { from: today, to: addDays(today, 29) };
    if (range === 'year') return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
    return { from: first, to: monthEnd(first) };
  }, [today, range]);
  const [r, setR] = useState<Report | null>(null);
  const { fail } = useUi();
  useEffect(() => {
    if (!span) return;
    setR(null);
    action<Report>('hotel.report', span).then(setR, fail);
  }, [span, fail]);
  const peak = r ? Math.max(1, ...r.days.map((d) => d.rooms)) : 1;
  return (
    <>
      <div className="toolbar">
        <div className="segmented tabs" role="tablist" aria-label={t('htl.period')}>
          {(['month', 'last', 'next', 'year'] as const).map((k) => (
            <button key={k} role="tab" aria-selected={range === k} className={range === k ? 'active' : ''} onClick={() => setRange(k)}>
              {t(`htl.r_${k}` as MessageKey)}
            </button>
          ))}
        </div>
        {r ? (
          <Button
            icon="download"
            onClick={() =>
              downloadCsv(
                `occupancy-${r.from}-${r.to}.csv`,
                [t('htl.day'), t('htl.c_rooms'), t('htl.sold'), t('htl.on_books'), t('htl.k_occupancy'), t('htl.room_revenue')],
                r.days.map((d) => [d.date, d.rooms, d.sold, d.on_books, d.rooms ? Math.round(((d.sold + d.on_books) / d.rooms) * 1000) / 10 : 0, lek(d.revenue) || '0']),
              )
            }
          >
            {t('htl.export')}
          </Button>
        ) : null}
      </div>
      {!r ? (
        <Skeleton h={360} r="var(--radius-lg)" />
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('htl.at_a_glance')}>
            <Kpi label={t('htl.k_occupancy')} value={`${r.occupancy}%`} hint={t('htl.sold_of', { n: r.sold, of: r.rooms * r.nights })} />
            <Kpi label={t('htl.room_revenue')} value={money(r.revenue)} hint={r.on_books ? t('htl.on_books_hint', { n: r.on_books, amount: money(r.revenue_on_books) }) : t('htl.extras_hint', { amount: money(r.extras) })} />
            <Kpi label={t('htl.adr')} value={money(r.adr)} hint={t('htl.adr_hint')} />
            <Kpi label={t('htl.revpar')} value={money(r.revpar)} hint={t('htl.revpar_hint')} />
            <Kpi label={t('htl.average_stay')} value={r.average_stay ? String(r.average_stay) : '—'} hint={[r.cancelled ? tn('htl.n_cancelled', r.cancelled) : null, r.no_shows ? tn('htl.n_no_shows', r.no_shows) : null].filter(Boolean).join(' · ') || t('htl.average_stay_hint')} warn={r.no_shows > 0} />
          </section>
          <section className="panel" aria-labelledby="htl-occ">
            <h2 id="htl-occ" className="rep-sub">
              {t('htl.occupancy_by_day')}
            </h2>
            <div className="htl-chart" role="img" aria-label={t('htl.occupancy_chart', { from: day(r.from), to: day(r.to), pct: r.occupancy })}>
              {r.days.map((d) => (
                <span key={d.date} className={`htl-col${d.date === r.today ? ' today' : ''}`} title={`${day(d.date)} · ${d.sold + d.on_books}/${d.rooms}`}>
                  <i className="sold" style={{ height: `${(d.sold / peak) * 100}%` }} />
                  <i className="books" style={{ height: `${(d.on_books / peak) * 100}%` }} />
                </span>
              ))}
            </div>
            <div className="htl-cal-legend muted small">
              <span>
                <i className="htl-dot htl-bar-checked_out" /> {t('htl.sold')}
              </span>
              <span>
                <i className="htl-dot htl-bar-booked" /> {t('htl.on_books')}
              </span>
              <span className="tabular">
                {day(r.from)} – {day(r.to)}
              </span>
            </div>
          </section>
          <div className="htl-two">
            <section className="panel" aria-labelledby="htl-bytype">
              <h2 id="htl-bytype" className="rep-sub">
                {t('htl.by_type')}
              </h2>
              <Table
                head={[t('htl.room_type'), t('htl.c_rooms'), t('htl.sold'), t('htl.k_occupancy'), t('htl.room_revenue')]}
                phoneHide={[1]}
                left={[0]}
                rows={r.by_type.map((x) => ({ key: x.type, cells: [<span className="strong">{x.name}</span>, <span className="tabular">{x.rooms}</span>, <span className="tabular">{x.sold}</span>, <span className="tabular">{x.occupancy}%</span>, <span className="tabular">{money(x.revenue)}</span>] }))}
              />
            </section>
            <section className="panel" aria-labelledby="htl-bysource">
              <h2 id="htl-bysource" className="rep-sub">
                {t('htl.by_source')}
              </h2>
              {r.by_source.length ? (
                <Table
                  head={[t('htl.source'), t('htl.c_stays'), t('htl.c_nights'), t('htl.room_revenue')]}
                  left={[0]}
                  rows={r.by_source.map((x) => ({ key: x.source, cells: [<span className="strong">{t(`htl.src_${x.source}` as MessageKey)}</span>, <span className="tabular">{x.stays}</span>, <span className="tabular">{x.nights}</span>, <span className="tabular">{money(x.revenue)}</span>] }))}
                />
              ) : (
                <p className="muted">{t('htl.no_sold')}</p>
              )}
            </section>
          </div>
        </>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- Set-up */

function SetupView() {
  const { data, reload } = useAction<Setup>('hotel.setup');
  const [edit, setEdit] = useState<null | { type: RoomType | null } | { room: Room | null } | { rate: Rate | null }>(null);
  if (!data) return <Skeleton h={360} r="var(--radius-lg)" />;
  const typeName = (id: string) => data.types.find((x) => x.id === id)?.name ?? '';
  const done = async () => {
    setEdit(null);
    await reload();
  };
  const section = (title: string, hint: string, add: string, onAdd: () => void, body: ReactNode) => (
    <section className="panel htl-setup">
      <div className="prot-history-head">
        <div>
          <h2 className="rep-sub">{title}</h2>
          <p className="muted small">{hint}</p>
        </div>
        <Button icon="plus" onClick={onAdd}>
          {add}
        </Button>
      </div>
      {body}
    </section>
  );
  return (
    <>
      {section(
        t('htl.room_types'),
        t('htl.room_types_hint'),
        t('htl.new_type'),
        () => setEdit({ type: null }),
        data.types.length ? (
          <Table
            head={[t('htl.room_type'), t('htl.code'), t('htl.sleeps_col'), t('htl.base_price'), t('htl.vat'), t('htl.c_rooms')]}
            phoneHide={[1, 2, 4]}
            left={[0, 1]}
            rows={data.types.map((x) => ({
              key: x.id,
              onClick: () => setEdit({ type: x }),
              cells: [
                <span className="vat-party">
                  <span className={x.active ? 'strong' : 'muted'}>{x.name}</span>
                  <span className="muted">{x.active ? x.beds : t('htl.not_sold')}</span>
                </span>,
                <span className="tabular">{x.code}</span>,
                <span className="tabular">{x.capacity}</span>,
                <span className="tabular">{money(x.base_price)}</span>,
                <span className="tabular">{x.vat}%</span>,
                <span className="tabular">{data.rooms.filter((r) => r.type === x.id && r.active).length}</span>,
              ],
            }))}
          />
        ) : (
          <EmptyState icon="bed" title={t('htl.no_types')} body={t('htl.no_types_body')} />
        ),
      )}
      {section(
        t('htl.rooms'),
        t('htl.rooms_hint'),
        t('htl.new_room'),
        () => setEdit({ room: null }),
        data.rooms.length ? (
          <Table
            head={[t('htl.room'), t('htl.room_type'), t('htl.floor'), t('htl.c_state')]}
            left={[0, 1]}
            rows={data.rooms.map((r) => ({
              key: r.id,
              onClick: () => setEdit({ room: r }),
              cells: [<span className={`tabular ${r.active ? 'strong' : 'muted'}`}>{r.number}</span>, <span>{typeName(r.type)}</span>, <span>{r.floor ?? '—'}</span>, r.active ? <RoomBadge r={r} /> : <span className="muted">{t('htl.not_in_use')}</span>],
            }))}
          />
        ) : (
          <EmptyState icon="key" title={t('htl.no_rooms')} />
        ),
      )}
      {section(
        t('htl.seasons'),
        t('htl.seasons_hint'),
        t('htl.new_season'),
        () => setEdit({ rate: null }),
        data.rates.length ? (
          <Table
            head={[t('htl.season'), t('htl.c_dates'), t('htl.price_night'), t('htl.min_nights')]}
            phoneHide={[3]}
            left={[0, 1]}
            rows={data.rates.map((x) => ({
              key: x.id,
              onClick: () => setEdit({ rate: x }),
              cells: [
                <span className="vat-party">
                  <span className={x.active ? 'strong' : 'muted'}>{x.name}</span>
                  <span className="muted">{typeName(x.room_type)}</span>
                </span>,
                <span className="vat-party">
                  <span className="tabular">
                    {short(x.starts)} – {short(x.ends)}
                  </span>
                  <span className="muted tabular">{x.starts.slice(0, 4) === x.ends.slice(0, 4) ? x.starts.slice(0, 4) : `${x.starts.slice(0, 4)}–${x.ends.slice(0, 4)}`}</span>
                </span>,
                <span className="tabular">{money(x.price)}</span>,
                <span className="tabular">{x.min_nights > 1 ? x.min_nights : '—'}</span>,
              ],
            }))}
          />
        ) : (
          <EmptyState icon="calendar" title={t('htl.no_seasons')} body={t('htl.no_seasons_body')} />
        ),
      )}
      {edit && 'type' in edit ? <TypeForm type={edit.type} onClose={() => setEdit(null)} onDone={done} /> : null}
      {edit && 'room' in edit ? <RoomForm room={edit.room} setup={data} onClose={() => setEdit(null)} onDone={done} /> : null}
      {edit && 'rate' in edit ? <RateForm rate={edit.rate} setup={data} onClose={() => setEdit(null)} onDone={done} /> : null}
    </>
  );
}

function TypeForm({ type, onClose, onDone }: { type: RoomType | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(type?.name ?? '');
  const [code, setCode] = useState(type?.code ?? '');
  const [price, setPrice] = useState(type ? lek(type.base_price) : '');
  const [vat, setVat] = useState<number | ''>(type?.vat ?? '');
  const [capacity, setCapacity] = useState(type?.capacity ?? 2);
  const [beds, setBeds] = useState(type?.beds ?? '');
  const [description, setDescription] = useState(type?.description ?? '');
  const [active, setActive] = useState(type?.active ?? true);
  const [tried, setTried] = useState(false);
  const minor = parseMoney(price);
  const errors = {
    name: name.trim() ? null : t('form.required'),
    code: /^[A-Za-z0-9-]{1,10}$/.test(code.trim()) ? null : t('htl.code_wrong'),
    price: minor === null || minor < 0 ? t('htl.amount_wrong') : null,
    vat: vat === '' ? t('htl.vat_choose') : null,
  };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    const ok = await run(
      () => action('hotel.save_type', { id: type?.id, name: name.trim(), code: code.trim(), base_price: minor, vat, capacity, beds: beds.trim() || null, description: description.trim() || null, sequence: type?.sequence ?? 0, active }),
      t('htl.saved'),
    );
    if (ok) await onDone();
  };
  return (
    <Dialog
      title={type ? type.name : t('htl.new_type')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('htl.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('htl.type_name')} error={tried ? errors.name : null} required>
            <input className="input" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
          </Field>
          <Field label={t('htl.code')} hint={t('htl.code_hint')} error={tried ? errors.code : null} required>
            <input className="input tabular" maxLength={10} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          </Field>
          <Field label={t('htl.base_price')} hint={t('htl.base_price_hint')} error={tried ? errors.price : null} required>
            <input className="input tabular" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
          </Field>
          <Field label={t('htl.vat')} hint={t('htl.vat_hint')} error={tried ? errors.vat : null} required>
            <TaxRateSelect value={vat} placeholder={t('htl.choose')} onChange={setVat} />
          </Field>
          <Field label={t('htl.sleeps_col')}>
            <input className="input tabular" type="number" min={1} max={20} value={capacity} onChange={(e) => setCapacity(Math.max(1, Number(e.target.value) || 1))} />
          </Field>
          <Field label={t('htl.beds')}>
            <input className="input" maxLength={80} value={beds} onChange={(e) => setBeds(e.target.value)} />
          </Field>
        </div>
        <Field label={t('htl.description')}>
          <textarea className="input" rows={2} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <label className="check-row">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('htl.type_active')}
        </label>
      </div>
    </Dialog>
  );
}

function RoomForm({ room, setup, onClose, onDone }: { room: Room | null; setup: Setup; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [number, setNumber] = useState(room?.number ?? '');
  const [type, setType] = useState(room?.type ?? setup.types.find((x) => x.active)?.id ?? '');
  const [floor, setFloor] = useState(room?.floor ?? '');
  const [location, setLocation] = useState(room?.location ?? '');
  const [note, setNote] = useState(room?.note ?? '');
  const [active, setActive] = useState(room?.active ?? true);
  const [tried, setTried] = useState(false);
  const errors = { number: number.trim() ? null : t('form.required'), type: type ? null : t('htl.choose_type') };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    if (await run(() => action('hotel.save_room', { id: room?.id, number: number.trim(), type, floor: floor.trim() || null, location: location || null, note: note.trim() || null, active }), t('htl.saved'))) await onDone();
  };
  return (
    <Dialog
      title={room ? t('htl.room_x', { room: room.number }) : t('htl.new_room')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('htl.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {room?.stays ? <p className="callout"><Icon name="calendar" size={16} /><span>{tn('htl.room_has_stays', room.stays)}</span></p> : null}
        <div className="form-grid">
          <Field label={t('htl.room_number')} error={tried ? errors.number : null} required>
            <input className="input tabular" maxLength={12} value={number} onChange={(e) => setNumber(e.target.value)} data-autofocus />
          </Field>
          <Field label={t('htl.room_type')} error={tried ? errors.type : null} required>
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">{t('htl.choose')}</option>
              {setup.types.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('htl.floor')}>
            <input className="input" maxLength={20} value={floor} onChange={(e) => setFloor(e.target.value)} />
          </Field>
          {setup.locations.length > 1 ? (
            <Field label={t('htl.location')}>
              <select className="input" value={location} onChange={(e) => setLocation(e.target.value)}>
                <option value="">{t('htl.main_location')}</option>
                {setup.locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>
        <Field label={t('htl.note')}>
          <input className="input" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <label className="check-row">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('htl.room_active')}
        </label>
      </div>
    </Dialog>
  );
}

function RateForm({ rate, setup, onClose, onDone }: { rate: Rate | null; setup: Setup; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [type, setType] = useState(rate?.room_type ?? setup.types[0]?.id ?? '');
  const [name, setName] = useState(rate?.name ?? '');
  const [starts, setStarts] = useState(rate?.starts ?? '');
  const [ends, setEnds] = useState(rate?.ends ?? '');
  const [price, setPrice] = useState(rate ? lek(rate.price) : '');
  const [minNights, setMinNights] = useState(rate?.min_nights ?? 1);
  const [active, setActive] = useState(rate?.active ?? true);
  const [tried, setTried] = useState(false);
  const [del, setDel] = useState(false);
  const minor = parseMoney(price);
  const errors = {
    type: type ? null : t('htl.choose_type'),
    name: name.trim() ? null : t('form.required'),
    dates: !starts || !ends ? t('form.required') : ends < starts ? t('htl.season_dates') : null,
    price: !price.trim() || minor === null || minor < 0 ? t('htl.amount_wrong') : null,
  };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    if (await run(() => action('hotel.save_rate', { id: rate?.id, room_type: type, name: name.trim(), starts, ends, price: minor, min_nights: minNights, active }), t('htl.saved'))) await onDone();
  };
  if (del && rate) {
    return (
      <Confirm
        title={t('htl.delete_season')}
        body={t('htl.delete_season_body', { name: rate.name })}
        confirm={t('htl.delete')}
        danger
        busy={busy}
        onClose={() => setDel(false)}
        onConfirm={() => void run(async () => (await action('hotel.delete_rate', { id: rate.id }), await onDone()), t('htl.deleted'))}
      />
    );
  }
  return (
    <Dialog
      title={rate ? rate.name : t('htl.new_season')}
      onClose={onClose}
      footer={
        <>
          {rate ? (
            <span className="foot-actions">
              <Button variant="ghost" icon="trash" onClick={() => setDel(true)}>
                {t('htl.delete')}
              </Button>
            </span>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('htl.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted small">{t('htl.season_note')}</p>
        <div className="form-grid">
          <Field label={t('htl.season_name')} error={tried ? errors.name : null} required>
            <input className="input" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('htl.season_placeholder')} data-autofocus />
          </Field>
          <Field label={t('htl.room_type')} error={tried ? errors.type : null} required>
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              {setup.types.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('htl.starts')} error={tried ? errors.dates : null} required>
            <input className="input" type="date" value={starts} onChange={(e) => setStarts(e.target.value)} />
          </Field>
          <Field label={t('htl.ends')} required>
            <input className="input" type="date" value={ends} min={starts || undefined} onChange={(e) => setEnds(e.target.value)} />
          </Field>
          <Field label={t('htl.price_night')} error={tried ? errors.price : null} required>
            <input className="input tabular" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
          </Field>
          <Field label={t('htl.min_nights')} hint={t('htl.min_nights_hint')}>
            <input className="input tabular" type="number" min={1} max={30} value={minNights} onChange={(e) => setMinNights(Math.max(1, Number(e.target.value) || 1))} />
          </Field>
        </div>
        <label className="check-row">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('htl.season_active')}
        </label>
      </div>
    </Dialog>
  );
}
